import { ATTRIBUTION_NAMES } from "@/constants/User";
import type { ATTRIBUTION } from "@/constants/User";
import { Guide } from "./Guides";
import type { FC } from "react";

export type GuideTopic = {
	id: string;
	title: string;
	subtitle: string;
	content: FC;
	roles?: readonly ATTRIBUTION[];
};

export type GuideTheme = {
	prefixId: string;
	theme: string;
	topics: readonly GuideTopic[];
};

function makeGuides<const TThemes extends readonly GuideTheme[]>(
	themes: TThemes
) {
	return themes.map((theme) => ({
		...theme,
		topics: theme.topics.map((topic) => ({
			...topic,
			id: `${theme.prefixId}-${topic.id}`,
		})),
	}));
}

export const GUIDE_MAPPING = makeGuides([
	{
		prefixId: "auth",
		theme: "Autenticação",
		topics: [
			{
				id: "access-management",
				title: "Gerenciamento de acesso",
				subtitle: "Processo de gerenciamento de acesso",
				content: Guide.Auth.AccessManagement,
			},
		],
	},
	{
		prefixId: "organizations",
		theme: "Organizações",
		topics: [
			{
				id: "definition",
				title: "Organizações",
				subtitle: "Definição e regras de organização",
				content: Guide.Organizations.Definition,
			},
			{
				id: "management",
				title: "Gerenciamento de organização",
				subtitle: "Processo de gerenciamento de organização",
				content: Guide.Organizations.Management,
				roles: [ATTRIBUTION_NAMES.ORGANIZACAO, ATTRIBUTION_NAMES.ADMIN],
			},
		],
	},
	{
		prefixId: "principles",
		theme: "Princípios Éticos",
		topics: [
			/* {
				id: "definition",
				title: "Definição",
				subtitle: "Definição de princípios",
				content: Guide.Principles.Definition,
			}, */
			{
				id: "management",
				title: "Gerenciamento de princípios",
				subtitle: "Processo de gerenciamento de princípios",
				content: Guide.Principles.Management,
				roles: [
					ATTRIBUTION_NAMES.ADMIN,
					ATTRIBUTION_NAMES.SOCIO_JURIDICO,
					ATTRIBUTION_NAMES.ETICO,
					ATTRIBUTION_NAMES.ETICISTA,
					ATTRIBUTION_NAMES.COMITE,
				],
			},
			{
				id: "x-questions",
				title: "Perguntas x Princípios Éticos",
				subtitle: "Relacionamento entre princípios e questões",
				content: Guide.Principles.PrinciplesXQuestions,
				roles: [
					ATTRIBUTION_NAMES.ADMIN,
					ATTRIBUTION_NAMES.SOCIO_JURIDICO,
					ATTRIBUTION_NAMES.ETICO,
				],
			},
			{
				id: "impact-calculation",
				title: "Cálculo de impacto ético",
				subtitle: "Processo de cálculo de impacto",
				content: Guide.Principles.ImpactCalculation,
				roles: [
					ATTRIBUTION_NAMES.ADMIN,
					ATTRIBUTION_NAMES.SOCIO_JURIDICO,
					ATTRIBUTION_NAMES.ETICO,
					ATTRIBUTION_NAMES.ETICISTA,
					ATTRIBUTION_NAMES.COMITE,
				],
			},
		],
	},
	{
		prefixId: "questions",
		theme: "Perguntas",
		topics: [
			/* {
				id: "definition",
				title: "Definição",
				subtitle: "Definição de perguntas",
				content: Guide.Questions.Definition,
			}, */
			{
				id: "management",
				title: "Gerenciamento de perguntas",
				subtitle: "Processo de gerenciamento de perguntas",
				content: Guide.Questions.Management,
				roles: [
					ATTRIBUTION_NAMES.ADMIN,
					ATTRIBUTION_NAMES.SOCIO_JURIDICO,
					ATTRIBUTION_NAMES.ETICO,
				],
			},
		],
	},
	{
		prefixId: "sections",
		theme: "Seções",
		topics: [
			{
				id: "definition",
				title: "Definição",
				subtitle: "Definição de seções",
				content: Guide.Sections.Definition,
			},
			{
				id: "management",
				title: "Gerenciamento de seções",
				subtitle: "Processo de gerenciamento de seções",
				content: Guide.Sections.Management,
				roles: [
					ATTRIBUTION_NAMES.ADMIN,
					ATTRIBUTION_NAMES.SOCIO_JURIDICO,
					ATTRIBUTION_NAMES.ETICO,
				],
			},
		],
	},
	{
		prefixId: "aias",
		theme: "AIAs",
		topics: [
			/* {
				id: "definition",
				title: "Definição",
				subtitle: "Ferramenta de análise objetiva de impactos",
				content: Guide.AIAs.Definition,
			}, */
			{
				id: "management",
				title: "Gerenciamento de AIA",
				subtitle: "Gerenciamento e funcionalidades possíveis para AIA",
				content: Guide.AIAs.Management,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.SOCIO_JURIDICO],
			},
			{
				id: "assessments",
				title: "Perguntas de enquadramento",
				subtitle: "Definições e regras de negócios",
				content: Guide.AIAs.Assessments,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.SOCIO_JURIDICO],
			},
			{
				id: "questionnaire-register",
				title: "Cadastro de questionário AIA",
				subtitle: "Processo de registro de questionário AIA",
				content: Guide.AIAs.QuestionnaireRegister,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.SOCIO_JURIDICO],
			},
		],
	},
	{
		prefixId: "eps",
		theme: "EPS",
		topics: [
			/* {
				id: "definition",
				title: "Definição",
				subtitle: "Definição de EPS",
				content: Guide.EPS.Definition,
			}, */
			{
				id: "register",
				title: "Gerenciamento de EPS",
				subtitle: "Gerenciamento e funcionalidades possíveis para EPS",
				content: Guide.EPS.Management,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.ETICO],
			},
			{
				id: "questionnaire-register",
				title: "Cadastro de questionário EPS",
				subtitle: "Processo de cadastro de questionários EPS",
				content: Guide.EPS.QuestionnaireRegister,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.ETICO],
			},
		],
	},
	{
		prefixId: "applications",
		theme: "Aplicações",
		topics: [
			{
				id: "register",
				title: "Gerenciamento de aplicação",
				subtitle: "Processo e funcionalidades de gestão de aplicações",
				content: Guide.Applications.Management,
				roles: [ATTRIBUTION_NAMES.ORGANIZACAO],
			},
			{
				id: "evaluation",
				title: "Avaliação de aplicações",
				subtitle: "Processo de avaliação de aplicações",
				content: Guide.Applications.Evaluation,
				roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.ETICISTA, ATTRIBUTION_NAMES.COMITE],
			},
		],
	},
	{
		prefixId: "methodology",
		theme: "Metodologia",
		topics: [
			{
				id: "application-flow",
				title: "Fluxo de aplicações",
				subtitle: "Processo de fluxo de aplicações",
				content: Guide.Methodology.ApplicationFlow,
			},
		],
	},
]);

export type GuideId = (typeof GUIDE_MAPPING)[number]["topics"][number]["id"];
